import React, { useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Magnetic from './ux/Magnetic';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const { FiQuote } = FiIcons;

const testimonials = [
  {
    quote: "Aetrix Logic completely transformed how we manage our production floor. What used to take three people manually is now automated. The system they built just works.",
    role: "Operations Director",
    industry: "Manufacturing Industry",
    tag: "ERP & Production Software"
  },
  {
    quote: "We came to them with a vague idea and they turned it into a fully working platform in weeks. Their understanding of both the technical and business side is rare.",
    role: "Founder",
    industry: "Retail Tech Startup",
    tag: "Custom Software Development"
  },
  {
    quote: "The AI integration they delivered reduced our data processing time by over 60%. They didn't just build what we asked for — they improved on it.",
    role: "Head of Operations",
    industry: "Logistics Company",
    tag: "AI & Automation"
  },
  {
    quote: "Professional, communicative, and technically excellent. They worked under NDA and we felt completely confident in them throughout.",
    role: "IT Manager",
    industry: "Healthcare Organisation",
    tag: "Confidential Engagement"
  },
  {
    quote: "We've worked with several development agencies before. Aetrix Logic is the first team that actually understood our workflow before writing any code.",
    role: "Director",
    industry: "Finance Services Firm",
    tag: "Consulting & Development"
  }
];

const Testimonials = () => {
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef([]);

  const pushCard = (el) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(titleRef.current,
        { autoAlpha: 0, y: 30 },
        { autoAlpha: 1, y: 0, duration: 0.8, ease: "power3.out", scrollTrigger: { trigger: titleRef.current, start: "top 85%" } }
      );

      gsap.fromTo(cardsRef.current,
        { autoAlpha: 0, y: 50 },
        { 
          autoAlpha: 1, 
          y: 0, 
          duration: 0.8, 
          stagger: 0.1, 
          ease: "back.out(1.2)",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 70%"
          }
        }
      );
    });

    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="py-24 relative z-10 bg-slate-50 dark:bg-slate-900 overflow-hidden transition-colors duration-300">
      {/* Background Accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-cyan-100/30 dark:bg-cyan-900/10 blur-[120px] rounded-full pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div ref={titleRef} style={{ opacity: 0 }} className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            What Our <span className="text-gradient-cyan">Clients Say</span>
          </h2>
          <div className="w-20 h-1 bg-cyan-500 mx-auto mb-6"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, index) => (
            <div key={index} ref={pushCard} style={{ opacity: 0 }} className={`${
              index >= 3 ? 'lg:col-span-1 lg:translate-x-1/2' : ''
            } ${index === 4 ? 'lg:translate-x-[50%]' : ''}`}>
              <Magnetic strength={0.1}>
                <div className="h-full p-8 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:border-cyan-300 dark:hover:border-cyan-500 transition-all duration-500 group flex flex-col justify-between shadow-sm hover:shadow-xl hover:-translate-y-2 cursor-default" data-cursor="hover">
                  <div>
                    <div className="w-10 h-10 bg-cyan-50 dark:bg-cyan-900/30 flex items-center justify-center mb-6 text-cyan-600 dark:text-cyan-400">
                      <SafeIcon icon={FiQuote} className="text-xl" />
                    </div>
                    <p className="text-slate-700 dark:text-slate-300 italic leading-relaxed mb-8 text-lg group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                      "{t.quote}"
                    </p>
                  </div>
                  
                  <div>
                    <div className="mb-4">
                      <span className="text-xs font-black uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 px-3 py-1 border border-cyan-200 dark:border-cyan-800">
                        {t.tag}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-slate-900 dark:text-white font-bold text-sm uppercase tracking-wider">{t.role}</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-xs font-medium uppercase tracking-widest mt-1">{t.industry}</p>
                    </div>
                  </div>
                </div>
              </Magnetic>
            </div>
          ))}
        </div>

        <p className="text-center mt-16 text-slate-400 dark:text-slate-500 text-xs font-bold uppercase tracking-[0.3em]">
          All client details kept confidential under NDA
        </p>
      </div>
    </section>
  );
};

export default Testimonials;